import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class MarkNewsAsReadParams {
  @IsUUID()
  @IsNotEmpty()
  news_id: string;
}

export class GetNewsParams {
  @IsUUID()
  @IsNotEmpty()
  news_id: string;
}

export class DeleteEditorNewsParams {
  @IsUUID()
  @IsNotEmpty()
  news_id: string;
}

export class UpdateEditorNewsParams {
  @IsUUID()
  @IsNotEmpty()
  news_id: string;
}

export class GetEditorNewsParams {
  @IsUUID()
  @IsNotEmpty()
  news_id: string;
}

export class GetAuthorsFilterOptionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetCategoriesFilterOptionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetAccountsFilterOptionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetEditorCompaniesQuery {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  account_ids?: string[];
}

export class GetCategoriesQuery {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  account_ids?: string[];
}

export class WidgetsInNews {
  @IsString()
  @IsNotEmpty()
  widget_name: string;

  @IsIn(['top_news', 'posts_feed'])
  @IsString()
  @IsNotEmpty()
  widget_type: 'top_news' | 'posts_feed';

  @IsString()
  @IsOptional()
  account_name?: string;

  @IsString()
  @IsOptional()
  company_name?: string;
}

export class NewsEditorListActions {
  @IsBoolean()
  @IsNotEmpty()
  delete: boolean;
}

export class NewsAuthor {
  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  second_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @IsString()
  @IsOptional()
  position?: string;
}

export type ParticipantGrade = string;

export type TargetAudienceType = string;

export class AccountItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class TargetAudienceItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account: AccountItem;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsIn(['dynamic', 'static'])
  @IsString()
  @IsNotEmpty()
  type: 'dynamic' | 'static';

  @IsIn([
    'empty',
    'less100',
    'less500',
    'less1000',
    'less2500',
    'less5000',
    'over5000',
  ])
  @IsString()
  @IsNotEmpty()
  participants_grade:
    | 'empty'
    | 'less100'
    | 'less500'
    | 'less1000'
    | 'less2500'
    | 'less5000'
    | 'over5000';
}

export type CryptoProPluginOpenType = string;

export class CompanyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  tsp_url?: string;

  @IsIn(['capi', 'cades'])
  @IsString()
  @IsOptional()
  crypto_pro_plugin_open_type?: 'capi' | 'cades';

  @IsIn(['api', 'browser_plugin'])
  @IsString()
  @IsOptional()
  hash_source?: 'api' | 'browser_plugin';
}

export class Category {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['gray', 'darkgray', 'green', 'orange', 'purple', 'blue', 'red'])
  @IsString()
  @IsNotEmpty()
  color: 'gray' | 'darkgray' | 'green' | 'orange' | 'purple' | 'blue' | 'red';
}

export type NewsStatus = string;

export class EditorNewsListItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account: AccountItem;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsIn(['draft', 'published', 'planned'])
  @IsString()
  @IsNotEmpty()
  status: 'draft' | 'published' | 'planned';

  @IsUUID()
  @IsNotEmpty()
  cover_att_id: string;

  @ValidateNested()
  @Type(() => Category)
  @IsOptional()
  category?: Category;

  @IsDateString()
  @IsOptional()
  published_at?: string;

  @ValidateNested({ each: true })
  @Type(() => CompanyItem)
  @IsArray()
  @IsNotEmpty()
  companies: CompanyItem[];

  @ValidateNested({ each: true })
  @Type(() => TargetAudienceItem)
  @IsArray()
  @IsNotEmpty()
  target_audiences: TargetAudienceItem[];

  @ValidateNested()
  @Type(() => NewsAuthor)
  @IsNotEmpty()
  author: NewsAuthor;

  @ValidateNested()
  @Type(() => NewsEditorListActions)
  @IsNotEmpty()
  actions: NewsEditorListActions;

  @IsBoolean()
  @IsNotEmpty()
  is_important: boolean;

  @ValidateNested({ each: true })
  @Type(() => WidgetsInNews)
  @IsArray()
  @IsOptional()
  widgets?: WidgetsInNews[];

  @IsBoolean()
  @IsNotEmpty()
  is_pinned: boolean;
}

export class EditorNewsListResponse {
  @ValidateNested({ each: true })
  @Type(() => EditorNewsListItem)
  @IsArray()
  @IsNotEmpty()
  news_list: EditorNewsListItem[];

  @IsInt()
  @IsNotEmpty()
  total: number;

  @IsInt()
  @IsOptional()
  total_draft?: number;

  @IsInt()
  @IsOptional()
  total_planned?: number;

  @IsInt()
  @IsOptional()
  total_published?: number;
}

export class NewsUserActions {
  @IsBoolean()
  @IsOptional()
  favorites?: boolean;
}

export class NewsActivityInfo {
  @IsInt()
  @IsNotEmpty()
  views_num: number;

  @IsInt()
  @IsOptional()
  comments_num?: number;

  @IsInt()
  @IsOptional()
  reactions_num?: number;
}

export class NewsListItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account: AccountItem;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  announcement?: string;

  @IsIn(['draft', 'published', 'planned'])
  @IsString()
  @IsNotEmpty()
  status: 'draft' | 'published' | 'planned';

  @IsUUID()
  @IsNotEmpty()
  cover_att_id: string;

  @ValidateNested()
  @Type(() => Category)
  @IsOptional()
  category?: Category;

  @IsDateString()
  @IsNotEmpty()
  published_at: string;

  @ValidateNested({ each: true })
  @Type(() => CompanyItem)
  @IsArray()
  @IsOptional()
  companies?: CompanyItem[];

  @ValidateNested()
  @Type(() => NewsActivityInfo)
  @IsNotEmpty()
  activity_info: NewsActivityInfo;

  @IsBoolean()
  @IsNotEmpty()
  is_important: boolean;

  @ValidateNested()
  @Type(() => NewsUserActions)
  @IsOptional()
  actions?: NewsUserActions;

  @IsBoolean()
  @IsNotEmpty()
  in_favorites: boolean;
}

export class NewsListResponse {
  @ValidateNested({ each: true })
  @Type(() => NewsListItem)
  @IsArray()
  @IsNotEmpty()
  news_list: NewsListItem[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class Reaction {
  @IsString()
  @IsNotEmpty()
  type: string;
}

export class ReactionItem {
  @ValidateNested()
  @Type(() => Reaction)
  @IsNotEmpty()
  reaction: Reaction;

  @IsInt()
  @IsNotEmpty()
  count: number;
}

export class EntityReactions {
  @ValidateNested({ each: true })
  @Type(() => ReactionItem)
  @IsArray()
  @IsNotEmpty()
  data: ReactionItem[];

  @IsString()
  @IsOptional()
  active_reaction_type?: string;
}

export class ContentSurveyActions {
  @IsBoolean()
  @IsNotEmpty()
  results: boolean;

  @IsBoolean()
  @IsNotEmpty()
  report: boolean;
}

export class UserAnswerMultipleTextChoice {
  @IsIn(['multiple_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'multiple_text_choice';

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  selected_options: string[];
}

export class UserAnswerSingleTextChoice {
  @IsIn(['single_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'single_text_choice';

  @IsUUID()
  @IsOptional()
  selected_option?: string;
}

export class UserAnswerFreeText {
  @IsIn(['free_text'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'free_text';

  @IsString()
  @IsNotEmpty()
  text: string;
}

export class ContentSurveyUserAnswerOneOf {
  @IsNotEmpty()
  object:
    | UserAnswerFreeText
    | UserAnswerSingleTextChoice
    | UserAnswerMultipleTextChoice;
}

export class ContentSurveyUserAnswer {
  @IsUUID()
  @IsNotEmpty()
  question_id: string;

  @ValidateNested()
  @Type(() => ContentSurveyUserAnswerOneOf)
  @IsOptional()
  user_answer?: ContentSurveyUserAnswerOneOf;
}

export class ContentSurveyUserPassing {
  @ValidateNested({ each: true })
  @Type(() => ContentSurveyUserAnswer)
  @IsArray()
  @IsNotEmpty()
  answers: ContentSurveyUserAnswer[];

  @IsInt()
  @IsNotEmpty()
  version: number;
}

export class FreeTextAnswerSettings {
  @IsIn(['free_text'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'free_text';

  @IsInt()
  @IsOptional()
  max_length?: number;

  @IsInt()
  @IsOptional()
  min_length?: number;
}

export class TextChoiceAnswerOption {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsIn(['text'])
  @IsString()
  @IsNotEmpty()
  type: 'text';

  @IsString()
  @IsNotEmpty()
  text_value: string;
}

export class MultipleTextChoiceAnswerSettings {
  @IsIn(['multiple_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'multiple_text_choice';

  @ValidateNested({ each: true })
  @Type(() => TextChoiceAnswerOption)
  @IsArray()
  @IsNotEmpty()
  answer_options: TextChoiceAnswerOption[];
}

export class SingleTextChoiceAnswerSettings {
  @IsIn(['single_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'single_text_choice';

  @ValidateNested({ each: true })
  @Type(() => TextChoiceAnswerOption)
  @IsArray()
  @IsNotEmpty()
  answer_options: TextChoiceAnswerOption[];
}

export class ContentSurveyQuestionAnswerSettings {
  @IsNotEmpty()
  object:
    | SingleTextChoiceAnswerSettings
    | MultipleTextChoiceAnswerSettings
    | FreeTextAnswerSettings;
}

export type AttachmentItemType = string;

export class AttachmentItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['image', 'file'])
  @IsString()
  @IsNotEmpty()
  type: 'image' | 'file';
}

export class BaseContentItem {
  @IsString()
  @IsOptional()
  data?: string;

  @ValidateNested({ each: true })
  @Type(() => AttachmentItem)
  @IsArray()
  @IsOptional()
  attachments?: AttachmentItem[];
}

export class ContentSurveyQuestion {
  @IsUUID()
  @IsOptional()
  id?: string;

  @ValidateNested()
  @Type(() => BaseContentItem)
  @IsNotEmpty()
  content: BaseContentItem;

  @ValidateNested()
  @Type(() => ContentSurveyQuestionAnswerSettings)
  @IsNotEmpty()
  answer_settings: ContentSurveyQuestionAnswerSettings;
}

export class ContentSurvey {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsBoolean()
  @IsNotEmpty()
  anonymous: boolean;

  @IsBoolean()
  @IsNotEmpty()
  show_result: boolean;

  @IsDateString()
  @IsOptional()
  completed_at?: string;

  @IsInt()
  @IsNotEmpty()
  version: number;

  @ValidateNested({ each: true })
  @Type(() => ContentSurveyQuestion)
  @IsArray()
  @IsNotEmpty()
  questions: ContentSurveyQuestion[];
}

export class ContentSurveyItem {
  @ValidateNested()
  @Type(() => ContentSurvey)
  @IsNotEmpty()
  survey: ContentSurvey;

  @IsInt()
  @IsOptional()
  passes_num?: number;

  @ValidateNested()
  @Type(() => ContentSurveyUserPassing)
  @IsOptional()
  passing?: ContentSurveyUserPassing;

  @ValidateNested()
  @Type(() => ContentSurveyActions)
  @IsOptional()
  actions?: ContentSurveyActions;
}

export class MentionItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['user', 'unit'])
  @IsString()
  @IsNotEmpty()
  type: 'user' | 'unit';
}

export class ContentItem {
  @IsString()
  @IsOptional()
  data?: string;

  @ValidateNested({ each: true })
  @Type(() => AttachmentItem)
  @IsArray()
  @IsOptional()
  attachments?: AttachmentItem[];

  @ValidateNested({ each: true })
  @Type(() => MentionItem)
  @IsArray()
  @IsOptional()
  mentions?: MentionItem[];

  @ValidateNested({ each: true })
  @Type(() => ContentSurveyItem)
  @IsArray()
  @IsOptional()
  surveys?: ContentSurveyItem[];
}

export class News {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account: AccountItem;

  @IsIn(['draft', 'published', 'planned'])
  @IsString()
  @IsNotEmpty()
  status: 'draft' | 'published' | 'planned';

  @IsDateString()
  @IsNotEmpty()
  published_at: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsNotEmpty()
  announcement: string;

  @IsUUID()
  @IsNotEmpty()
  cover_att_id: string;

  @ValidateNested()
  @Type(() => Category)
  @IsNotEmpty()
  category: Category;

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  tags: string[];

  @ValidateNested()
  @Type(() => ContentItem)
  @IsNotEmpty()
  content: ContentItem;

  @ValidateNested()
  @Type(() => NewsAuthor)
  @IsOptional()
  author?: NewsAuthor;

  @ValidateNested()
  @Type(() => NewsActivityInfo)
  @IsNotEmpty()
  activity_info: NewsActivityInfo;

  @IsBoolean()
  @IsNotEmpty()
  is_important: boolean;

  @IsBoolean()
  @IsNotEmpty()
  comments_enabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  reactions_enabled: boolean;

  @ValidateNested()
  @Type(() => EntityReactions)
  @IsOptional()
  reactions?: EntityReactions;

  @ValidateNested()
  @Type(() => NewsUserActions)
  @IsOptional()
  actions?: NewsUserActions;

  @IsBoolean()
  @IsOptional()
  in_favorites?: boolean;
}

export class FilterTimeRange {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['timerange'])
  @IsString()
  @IsNotEmpty()
  type: 'timerange';

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsIn(['till_now', 'any', 'from_now'])
  @IsString()
  @IsNotEmpty()
  allow_dates: 'till_now' | 'any' | 'from_now';

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterBool {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['bool'])
  @IsString()
  @IsNotEmpty()
  type: 'bool';

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;

  @IsString()
  @IsOptional()
  tooltip?: string;
}

export class FilterMultipleSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple-search'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple-search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['search'])
  @IsString()
  @IsNotEmpty()
  type: 'search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterText {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['text'])
  @IsString()
  @IsNotEmpty()
  type: 'text';

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterSelectBadge {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsIn(['blue', 'velvet', 'red'])
  @IsString()
  @IsOptional()
  color?: 'blue' | 'velvet' | 'red';

  @IsIn(['left', 'bottom'])
  @IsString()
  @IsOptional()
  position?: 'left' | 'bottom';
}

export class FilterSelectOption {
  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;

  @IsIn(['item'])
  @IsString()
  @IsOptional()
  type?: 'item';

  @ValidateNested()
  @Type(() => FilterSelectBadge)
  @IsOptional()
  badge?: FilterSelectBadge;
}

export class FilterMultiple {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple';

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;

  @IsBoolean()
  @IsOptional()
  autocomplete?: boolean;

  @IsBoolean()
  @IsOptional()
  clearable?: boolean;
}

export class FilterSelect {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['select'])
  @IsString()
  @IsNotEmpty()
  type: 'select';

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  autocomplete?: boolean;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class AvailableFilters {
  @IsArray()
  @IsNotEmpty()
  filters: (
    | FilterSelect
    | FilterMultiple
    | FilterText
    | FilterSearch
    | FilterMultipleSearch
    | FilterBool
    | FilterTimeRange
  )[];
}

export type NewsEditorAuthorsFilterOptionsResponse = FilterSelectOption[];

export class NewsCategoryFilterSelectOption {
  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;
}

export type NewsCategoriesFilterOptionsResponse =
  NewsCategoryFilterSelectOption[];

export type NewsEditorAccountsFilterOptionsResponse = FilterSelectOption[];

export class NewsEditorActions {
  @IsBoolean()
  @IsNotEmpty()
  delete: boolean;

  @IsBoolean()
  @IsNotEmpty()
  edit_scope: boolean;

  @IsBoolean()
  @IsNotEmpty()
  edit_publication_status: boolean;
}

export class NewsTargetAudienceAccessScopeResponse {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account: AccountItem;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsIn(['dynamic', 'static'])
  @IsString()
  @IsNotEmpty()
  type: 'dynamic' | 'static';

  @IsIn([
    'empty',
    'less100',
    'less500',
    'less1000',
    'less2500',
    'less5000',
    'over5000',
  ])
  @IsString()
  @IsNotEmpty()
  participants_grade:
    | 'empty'
    | 'less100'
    | 'less500'
    | 'less1000'
    | 'less2500'
    | 'less5000'
    | 'over5000';
}

export type UnitType = string;

export class UnitAccessScope {
  @IsUUID()
  @IsNotEmpty()
  unit_id: string;

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  unit_type?: 'operational' | 'legal';
}

export class NewsCompanyAccessScopeResponse {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @ValidateNested({ each: true })
  @Type(() => UnitAccessScope)
  @IsArray()
  @IsNotEmpty()
  units: UnitAccessScope[];
}

export class NewsSettingsResponse {
  @IsBoolean()
  @IsNotEmpty()
  show_author: boolean;

  @IsBoolean()
  @IsNotEmpty()
  show_to_editors: boolean;

  @IsBoolean()
  @IsNotEmpty()
  comments_enabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  reactions_enabled: boolean;

  @IsArray()
  @IsNotEmpty()
  access_scopes: (
    | NewsCompanyAccessScopeResponse
    | NewsTargetAudienceAccessScopeResponse
  )[];
}

export class EditorNews {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account: AccountItem;

  @IsIn(['draft', 'published', 'planned'])
  @IsString()
  @IsNotEmpty()
  status: 'draft' | 'published' | 'planned';

  @IsDateString()
  @IsOptional()
  published_at?: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsNotEmpty()
  announcement: string;

  @IsUUID()
  @IsNotEmpty()
  cover_att_id: string;

  @ValidateNested()
  @Type(() => Category)
  @IsNotEmpty()
  category: Category;

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  tags: string[];

  @ValidateNested()
  @Type(() => ContentItem)
  @IsNotEmpty()
  content: ContentItem;

  @ValidateNested()
  @Type(() => NewsAuthor)
  @IsNotEmpty()
  author: NewsAuthor;

  @ValidateNested()
  @Type(() => NewsSettingsResponse)
  @IsNotEmpty()
  settings: NewsSettingsResponse;

  @ValidateNested()
  @Type(() => NewsEditorActions)
  @IsNotEmpty()
  actions: NewsEditorActions;

  @IsBoolean()
  @IsNotEmpty()
  is_important: boolean;

  @ValidateNested({ each: true })
  @Type(() => WidgetsInNews)
  @IsArray()
  @IsOptional()
  widgets?: WidgetsInNews[];

  @IsBoolean()
  @IsNotEmpty()
  is_pinned: boolean;
}

export class NewsCreateResponse {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsOptional()
  name?: string;
}

export type EditorCompanies = CompanyItem[];

export class AccountAccessItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  full_access: boolean;

  @IsBoolean()
  @IsOptional()
  scope_disabled?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  target_audience_enabled: boolean;

  @ValidateNested({ each: true })
  @Type(() => CompanyItem)
  @IsArray()
  @IsOptional()
  companies?: CompanyItem[];
}

export type EditorAccounts = AccountAccessItem[];

export class CategoriesResponse {
  @ValidateNested({ each: true })
  @Type(() => Category)
  @IsArray()
  @IsNotEmpty()
  categories: Category[];
}

export class TimeRange {
  @IsDateString()
  @IsOptional()
  start?: string;

  @IsDateString()
  @IsOptional()
  end?: string;
}

export class NewsListRequestFilters {
  @IsIn(['all', 'published', 'planned', 'draft'])
  @IsString()
  @IsOptional()
  status?: 'all' | 'published' | 'planned' | 'draft';

  @IsString()
  @IsOptional()
  title_search?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  category_ids?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  author_ids?: string[];

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  published_at?: TimeRange;

  @IsIn(['important', 'all'])
  @IsString()
  @IsOptional()
  is_important?: 'important' | 'all';

  @IsBoolean()
  @IsOptional()
  is_pinned?: boolean;

  @IsBoolean()
  @IsOptional()
  pinned_on_main?: boolean;

  @IsIn(['all', 'my', 'important'])
  @IsString()
  @IsOptional()
  view_mode?: 'all' | 'my' | 'important';
}

export class Pagination {
  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class TopNewsListRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => NewsListRequestFilters)
  @IsOptional()
  filters?: NewsListRequestFilters;

  @IsUUID()
  @IsNotEmpty()
  widget_id: string;

  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  exclude_news_ids: string[];
}

export class NewsListRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => NewsListRequestFilters)
  @IsOptional()
  filters?: NewsListRequestFilters;
}

export class NewsTargetAudienceAccessScopeRequest {
  @IsIn(['target_audience'])
  @IsString()
  @IsNotEmpty()
  type: 'target_audience';

  @IsUUID()
  @IsNotEmpty()
  target_audience_id: string;
}

export class NewsCompanyAccessScopeRequest {
  @IsIn(['company'])
  @IsString()
  @IsNotEmpty()
  type: 'company';

  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  units_ids?: string[];
}

export class NewsSettingsRequest {
  @IsBoolean()
  @IsNotEmpty()
  show_author: boolean;

  @IsBoolean()
  @IsNotEmpty()
  show_to_editors: boolean;

  @IsBoolean()
  @IsNotEmpty()
  comments_enabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  reactions_enabled: boolean;

  @IsArray()
  @IsNotEmpty()
  access_scopes: (
    | NewsCompanyAccessScopeRequest
    | NewsTargetAudienceAccessScopeRequest
  )[];
}

export class NewsContent {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  announcement?: string;

  @IsUUID()
  @IsNotEmpty()
  cover_att_id: string;

  @IsUUID()
  @IsOptional()
  category_id?: string;

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  tags: string[];

  @ValidateNested()
  @Type(() => ContentItem)
  @IsNotEmpty()
  content_item: ContentItem;
}

export class NewsRequest {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;

  @IsIn(['draft', 'published', 'planned'])
  @IsString()
  @IsNotEmpty()
  new_status: 'draft' | 'published' | 'planned';

  @ValidateNested()
  @Type(() => NewsContent)
  @IsNotEmpty()
  content: NewsContent;

  @ValidateNested()
  @Type(() => NewsSettingsRequest)
  @IsNotEmpty()
  settings: NewsSettingsRequest;

  @IsDateString()
  @IsOptional()
  publish_at?: string;

  @IsBoolean()
  @IsNotEmpty()
  is_important: boolean;
}

@injectable()
export class NewsService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async getTopNewsList({ body }: { body: TopNewsListRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, TopNewsListRequest),
    ]);

    let requestPath = '/widget/top_news/news/list';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      EditorNewsListResponse,
    );

    return response;
  }

  async getNewsList({
    body = {},
  }: {
    body?: NewsListRequest;
  } = {}) {
    await Promise.all([this.validator.validateOrReject(body, NewsListRequest)]);

    let requestPath = '/news/list';

    const result = await this.http.post(requestPath, body, {
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      NewsListResponse,
    );

    return response;
  }

  async markNewsAsRead({ params }: { params: MarkNewsAsReadParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, MarkNewsAsReadParams),
    ]);

    let requestPath = '/news/{news_id}/mark_read';

    const paramsKeys = Object.keys(params) as (keyof MarkNewsAsReadParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, {});

    return result;
  }

  async getNews({ params }: { params: GetNewsParams }) {
    await Promise.all([this.validator.validateOrReject(params, GetNewsParams)]);

    let requestPath = '/news/{news_id}';

    const paramsKeys = Object.keys(params) as (keyof GetNewsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(result, News);

    return response;
  }

  async getAvailableNewsEditorFilters() {
    let requestPath = '/news/available_filters';

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      FiltersResponse,
    );

    return response;
  }

  async getAuthorsFilterOptions({
    query = {},
  }: {
    query?: GetAuthorsFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetAuthorsFilterOptionsQuery),
    ]);

    let requestPath = '/news/editor/authors/filter_options';

    const result = await this.http.get(requestPath, query, {});

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getCategoriesFilterOptions({
    query = {},
  }: {
    query?: GetCategoriesFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCategoriesFilterOptionsQuery),
    ]);

    let requestPath = '/news/categories/filter_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => NewsCategoryFilterSelectOption)
      response: NewsCategoryFilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getAccountsFilterOptions({
    query = {},
  }: {
    query?: GetAccountsFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetAccountsFilterOptionsQuery),
    ]);

    let requestPath = '/news/editor/accounts/filter_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async deleteEditorNews({ params }: { params: DeleteEditorNewsParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeleteEditorNewsParams),
    ]);

    let requestPath = '/news/editor/{news_id}';

    const paramsKeys = Object.keys(params) as (keyof DeleteEditorNewsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, {}, { withSide: true });

    return result;
  }

  async updateEditorNews({
    params,
    body,
  }: {
    params: UpdateEditorNewsParams;
    body: NewsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateEditorNewsParams),
      this.validator.validateOrReject(body, NewsRequest),
    ]);

    let requestPath = '/news/editor/{news_id}';

    const paramsKeys = Object.keys(params) as (keyof UpdateEditorNewsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async getEditorNews({ params }: { params: GetEditorNewsParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEditorNewsParams),
    ]);

    let requestPath = '/news/editor/{news_id}';

    const paramsKeys = Object.keys(params) as (keyof GetEditorNewsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, EditorNews);

    return response;
  }

  async getEditorList({
    body = {},
  }: {
    body?: NewsListRequest;
  } = {}) {
    await Promise.all([this.validator.validateOrReject(body, NewsListRequest)]);

    let requestPath = '/news/editor/list';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      EditorNewsListResponse,
    );

    return response;
  }

  async createNews({ body }: { body: NewsRequest }) {
    await Promise.all([this.validator.validateOrReject(body, NewsRequest)]);

    let requestPath = '/news/editor/create';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      NewsCreateResponse,
    );

    return response;
  }

  async getEditorCompanies({
    query = {},
  }: {
    query?: GetEditorCompaniesQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEditorCompaniesQuery),
    ]);

    let requestPath = '/news/editor/companies';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => CompanyItem)
      response: CompanyItem[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getEditorAccounts() {
    let requestPath = '/news/editor/accounts';

    const result = await this.http.get(requestPath, {}, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => AccountAccessItem)
      response: AccountAccessItem[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getCategories({
    query = {},
  }: {
    query?: GetCategoriesQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCategoriesQuery),
    ]);

    let requestPath = '/news/categories';

    const result = await this.http.get(requestPath, query, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      CategoriesResponse,
    );

    return response;
  }
}
